import classNames from 'classnames';
import React, { JSX, useEffect, useRef, useState } from 'react';
import StorroLogo from 'assets/storro-logo-and-text-light.svg';
import AgencyDriveLogo from 'assets/agency-drive-logo-white.svg';
import useScreenSize from '../../../hooks/UseScreenSize';
import { ClientPortalTheme } from '../../../types';
import { ScreenSize } from '../../../types/ScreenSize';
import Spinner, { SpinnerSize } from '../../Spinner';
import Advertise, { AdvertiseSize } from './Advertise';
import ClientPortalBackground from './Background';
import FooterBadges from 'components/QuickShare/Client/ShareView/FooterBadges';
import { InfoIcon, TextOutdentIcon } from '@phosphor-icons/react';
import useTheme from 'hooks/UseTheme';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  theme?: ClientPortalTheme;
  logoUrl?: string;
  loading?: boolean;
  loadingText?: string;
  preview?: boolean;
  sidebarDetails?: React.ReactNode;
  smallContentBox?: boolean; // set the content box to a max fixed size
  sidebarEnabled?: boolean;
  children: React.ReactNode;
}

export default function ClientPortal({
  theme,
  logoUrl,
  preview,
  children,
  sidebarDetails,
  loading,
  loadingText,
  smallContentBox,
  sidebarEnabled = true,
}: Props): JSX.Element {
  const [showSidebar, setShowSidebar] = useState<boolean>();
  const [showAds, setShowAds] = useState<boolean>();

  const sidbarRef = useRef<HTMLDivElement>(null);

  const { height, width } = useScreenSize();
  const { isStorroTheme, isAgencyTheme, companyName } = useTheme();
  const { t } = useTranslation();

  /**
   * Toggle the sidebar (for mobile)
   */
  useEffect(() => {
    // FIXME: REACT 19 - disable for now Error: Calling setState synchronously within an effect can trigger cascading renders
    // eslint-disable-next-line
    setShowSidebar(width > ScreenSize.md ? true : false);
  }, [width]);

  /**
   * Define if we need to show the ads block. Based on the calculation of the free space
   */
  useEffect(() => {
    if (!sidbarRef.current) return;
    // FIXME: REACT 19 - disable for now Error: Calling setState synchronously within an effect can trigger cascading renders
    // eslint-disable-next-line
    setShowAds(height && height > sidbarRef.current.clientHeight ? true : false);
  }, [height]);

  return (
    <div className='min-h-screen flex items-stretch'>
      <ClientPortalBackground theme={width > ScreenSize.xs ? theme : undefined} preview={preview}>
        <div
          className={classNames('h-full w-full flex flex-col', {
            '!h-[calc(100%-50px)]': preview,
          })}
        >
          <div className='grow flex items-center justify-center'>
            <div
              className={classNames('w-full sm:w-3/4 mb-3 sm:mb-0', 'md:w-full md:max-w-5xl  md:ml-[50px]', {
                'md:mr-[calc(300px+50px)] lg:mr-[calc(400px+50px)]': sidebarEnabled,
                '!max-w-[500px]': smallContentBox && width > 500,
              })}
            >
              {loading && (
                <div className='flex flex-col items-center justify-center'>
                  <Spinner inverseColor={false} size={SpinnerSize['2xlarge']} />
                  <span className='text-white mt-5'>{loadingText}</span>
                </div>
              )}

              {!loading && <div className='w-full bg-gray-100 shadow-xl rounded border p-5 sm:p-10'>{children}</div>}
            </div>
          </div>
          <div className='flex flex-col-reverse xl:flex-row sm:items-center text-white relative mb-10 lg:mb-4'>
            <div
              className={classNames(
                'hidden absolute xs:flex text-xs opacity-40 justify-center sm:justify-start',
                'top-20 sm:top-12 md:top-20 lg:top-1',
                'lg:translate-x-0 left-4',
              )}
            >
              {theme && theme.background && (
                <p>
                  <Trans
                    i18nKey='background-photo-credit'
                    defaults='Photo by <0>{{user}}</0> on <1>Unsplash</1>'
                    values={{ user: theme.background.user, link: theme.background.userLink }}
                    components={[
                      <a key='image' href='{{link}}?utm_source=storro&utm_medium=referral' rel='noreferrer' target='_blank'>
                        user
                      </a>,
                      <a key='link' href='https://unsplash.com/?utm_source=storro&utm_medium=referral' rel='noreferrer' target='_blank'>
                        Unsplash
                      </a>,
                    ]}
                  />
                </p>
              )}
            </div>
            <div className='flex flex-col-reverse sm:flex-row md:flex-col-reverse lg:flex-row justify-center w-full gap-x-10 gap-y-4'>
              <div className='flex items-center gap-x-2 justify-center opacity-75'>
                <img className='hidden xs:block' src={isStorroTheme ? StorroLogo : AgencyDriveLogo} alt='logo' width='75' />
                <span className='text-gray-200 text-xs lg:text-sm'>
                  {t('copyright', 'Copyright')} © {new Date().getFullYear()} {companyName} {'B.V.'}
                </span>
              </div>
              <FooterBadges includeLogo={false} className='justify-center' />
            </div>
          </div>
        </div>
      </ClientPortalBackground>

      {sidebarEnabled && (
        <div
          className={classNames(
            'p-5 fixed z-10 right-0 h-full flex justify-center items-center bg-gradient-to-r from-gray-100 to-white drop-shadow-lg transition-all animate-slow',
            {
              'w-[300px] lg:w-[400px]': showSidebar,
              'w-72 !-right-[290px] sm:!-right-[260px]': !showSidebar,
              'bg-gray-100': isStorroTheme,
              'bg-agency-background': isAgencyTheme,
            },
          )}
        >
          <button
            onClick={() => setShowSidebar(prevState => !prevState)}
            className={classNames(
              'md:hidden cursor-pointer bg-gray-100 absolute top-24 -left-10 h-10 w-10 flex items-center justify-center rounded-l',
            )}
          >
            {/* with -scale-x-[1] we flip the icon */}
            {showSidebar ? <TextOutdentIcon className='-scale-x-[1]' /> : <InfoIcon />}
          </button>

          <div ref={sidbarRef} className='w-[250px]'>
            {logoUrl && <img className='mt-10 mx-auto mb-16 max-w-[200px] max-h-[125px]' src={logoUrl} alt='Logo' />}

            {sidebarDetails}
            <Advertise
              size={AdvertiseSize.Small}
              inverseColor={true}
              className={classNames('mt-10 lg:mt-18 xl:mt-28 animate-fadeInUp animate-fast w-[250px]', {
                'animate-delay-[2s]': !preview,
                'animate-delay-[0.3s]': preview,
                hidden: !showAds,
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
